/**
 * Districts API Service
 *
 * SWR hooks for fetching district reference data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { District } from '../../shared/components'

interface DistrictResponse {
  id: number
  province_id: number
  name_th: string
  name_en: string
  code: string
  latitude: string
  longitude: string
  population: string | number
}

/**
 * Fetch all districts
 *
 * GET /districts
 */
export function useDistricts() {
  const { data, error, isLoading } = useSWR<DistrictResponse[]>(
    '/districts',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minute (districts don't change often)
    }
  )

  const districts: District[] =
    data?.map((d) => ({
      id: d.id,
      name_th: d.name_th,
      name_en: d.name_en,
      code: d.code,
      population: typeof d.population === 'string' ? parseInt(d.population) : d.population,
    })) || []

  return {
    data: districts,
    isLoading,
    error,
  }
}

/**
 * Fetch single district by ID
 *
 * GET /districts/{id}
 */
export function useDistrict(id: number | null) {
  const { data, error, isLoading } = useSWR(
    id ? `/districts/${id}` : null,
    apiClient.fetcher
  )

  return {
    data,
    isLoading,
    error,
  }
}
